// ============================================================================
// Feuille de LIMITES : les DÉVELOPPEMENTS LIMITÉS en 0 (refonte, lot 6.63, moteur v58).
// ============================================================================
// Une ligne écrit le développement d'une expression, avec un reste « o(xᵏ) » :
//
//   « e^x = 1 + x + x² ÷ 2 + o(x²) »                  DL de référence        2
//   « e^(x²) = 1 + x² + o(x³) »                        Composer les DL        2
//   « e^x − 1 − x = x² ÷ 2 + o(x²) »                   Somme de DL            1
//   « x × sin(x) = x² + o(x²) »                        Multiplier un DL       1
//   « sin(x)² = x² − x⁴ ÷ 3 + o(x⁴) »                  Produit de DL          2
//   « (e^x − 1 − x) ÷ x² = 1 ÷ 2 + o(1) »              Diviser par xᵏ         1
//   « (sin(x) − x) ÷ (x × sin(x)) = −x ÷ 6 + o(x) »    Quotient de DL         2
//   « (e^x − 1 − x) ÷ x² → 1 ÷ 2 »                     Lire la limite sur le DL 1
//
// Ce module est PUR : il dit d'où vient un développement (les gestes du cours) à partir de
// ce qui est déjà établi (une fonction `lookup` donne le DL connu d'une expression). Le juge
// (kernel-limit.ts) vérifie la vérité (kernel/series.ts) et attribue les HORA.
//
// Le compte des gestes est un « potentiel » : les gestes qu'il faudrait encore faire pour
// réduire une écriture à « P + o(xᵏ) ». Une ligne rapporte les gestes qu'elle consomme
// (potentiel de gauche moins potentiel de droite) : écrire d'un coup ou pas à pas rapporte
// la même chose.
// ============================================================================

import type { Term } from './kernel/term';
import * as Q from './kernel/exact';
import {
  hasO,
  oAtomOrder,
  oOrder,
  oSym,
  oText,
  sAdd,
  sInv,
  sMul,
  serFn,
  serPow,
  seriesOf,
  termRat,
  type Ser,
} from './kernel/series';

export type DLStep =
  | 'dlReference'
  | 'dlCompose'
  | 'dlSum'
  | 'dlScale'
  | 'dlProduct'
  | 'dlDivide'
  | 'dlQuotient';

/* ------------------------------ lecture des bouts « o(x²) » ------------------------------ */

const O_TEXT_RE =
  /(?<![A-Za-zÀ-ÿ])o\(\s*(1|([a-zA-Z])\s*(?:\^\s*\(?\s*(\d+)\s*\)?|([⁰¹²³⁴⁵⁶⁷⁸⁹]+))?)\s*\)/g;
const SUP = '⁰¹²³⁴⁵⁶⁷⁸⁹';

/**
 * Remplace chaque « o(x²) » du texte par un symbole « o₂ » (que le lexique lit d'un bloc) ;
 * `outs` garde l'écriture de chacun, dans l'ordre.
 */
export function withOPlaceholders(text: string): { text: string; outs: string[] } {
  const outs: string[] = [];
  const t = text.replace(
    O_TEXT_RE,
    (_m, body: string, v?: string, caret?: string, sup?: string) => {
      let k: number;
      if (body === '1') k = 0;
      else if (caret) k = Number(caret);
      else if (sup) k = Number([...sup].map((c) => SUP.indexOf(c)).join(''));
      else k = 1;
      outs.push(oText(k, v || 'x'));
      return ` ${oSym(k)} `;
    },
  );
  return { text: t, outs };
}

/** remet l'écriture « o(x²) » à la place des symboles « o₂ » (même ordre) */
export function restoreOAtoms(atoms: string[], outs: string[]): string[] {
  let i = 0;
  return atoms.map((a) => (oOrder(a) !== null ? (outs[i++] ?? a) : a));
}

/** les bouts « o(x²) » d'une ligne lus comme les symboles « o₂ » (pour le calcul) */
export function oAtomsToSyms(atoms: string[]): string[] {
  return atoms.map((a) => {
    const k = oAtomOrder(a);
    return k === null ? a : oSym(k);
  });
}

/** un texte qui écrit un reste « o(…) » */
export function textHasO(text: string): boolean {
  O_TEXT_RE.lastIndex = 0;
  const r = O_TEXT_RE.test(text);
  O_TEXT_RE.lastIndex = 0;
  return r;
}

/* ------------------------------ formes ------------------------------ */

export function hasX(t: Term, x: string): boolean {
  switch (t.k) {
    case 'sym':
      return t.n === x;
    case 'num':
      return false;
    case 'add':
    case 'mul':
      return t.c.some((c) => hasX(c, x));
    case 'fn':
      return hasX(t.a, x);
    default:
      return hasX(t.a, x) || hasX(t.b, x);
  }
}

/** l'ordre le plus grand d'un reste écrit (−∞ : aucun) */
export function maxOOrder(t: Term): number {
  switch (t.k) {
    case 'sym': {
      const k = oOrder(t.n);
      return k === null ? -Infinity : k;
    }
    case 'num':
      return -Infinity;
    case 'add':
    case 'mul':
      return Math.max(-Infinity, ...t.c.map(maxOOrder));
    case 'fn':
      return maxOOrder(t.a);
    default:
      return Math.max(maxOOrder(t.a), maxOOrder(t.b));
  }
}

/**
 * Un polynôme en x (ou une fraction dont le dénominateur est un monôme : 1 ÷ x, x ÷ 6) :
 * pas un geste de DL.
 */
export function isPolyLike(t: Term, x: string): boolean {
  switch (t.k) {
    case 'num':
      return true;
    case 'sym':
      return t.n === x;
    case 'add':
    case 'mul':
      return t.c.every((c) => isPolyLike(c, x));
    case 'div':
      return isPolyLike(t.a, x) && !!monomialOf(t.b, x);
    case 'pow': {
      const r = termRat(t.b);
      return !!r && Q.isInteger(r) && Q.sign(r) >= 0 && isPolyLike(t.a, x);
    }
    default:
      return false;
  }
}

/** un monôme exact c xᵈ (d entier, éventuellement négatif ; c ≠ 0) */
export function monomialOf(t: Term, x: string): { d: number; c: Q.Rat } | null {
  if (hasO(t)) return null;
  if (t.k === 'fn') return null;
  const s = seriesOf(t, x, 24);
  if (!s || s.e !== Infinity || s.co.size !== 1) return null;
  const first = [...s.co][0];
  if (!first) return null;
  const [d, c] = first;
  return { d, c };
}

const isOnePlusX = (u: Term, x: string): boolean =>
  u.k === 'add' &&
  u.c.length === 2 &&
  u.c.some((c) => c.k === 'sym' && c.n === x) &&
  u.c.some((c) => c.k === 'num' && Q.eq(c.v, Q.ONE));
const isOneMinusX = (u: Term, x: string): boolean => {
  if (u.k !== 'add' || u.c.length !== 2) return false;
  const s = seriesOf(u, x, 4);
  return (
    !!s &&
    s.e === Infinity &&
    s.co.size === 2 &&
    Q.eq(s.co.get(0) ?? Q.ZERO, Q.ONE) &&
    Q.eq(s.co.get(1) ?? Q.ZERO, Q.rat(-1))
  );
};

/** un DL de RÉFÉRENCE du cours (en la variable elle-même) */
export function isReference(t: Term, x: string): boolean {
  const isX = (u: Term) => u.k === 'sym' && u.n === x;
  if (t.k === 'fn') {
    if (['exp', 'sin', 'cos', 'tan', 'arctan'].includes(t.f)) return isX(t.a);
    if (t.f === 'ln' || t.f === 'sqrt') return isOnePlusX(t.a, x);
    return false;
  }
  if (t.k === 'pow') {
    const r = termRat(t.b);
    return !!r && !Q.isInteger(r) && isOnePlusX(t.a, x);
  }
  if (t.k === 'div') {
    const one = t.a.k === 'num' && Q.eq(t.a.v, Q.ONE);
    return one && (isOnePlusX(t.b, x) || isOneMinusX(t.b, x));
  }
  return false;
}

/**
 * Une écriture RÉDUITE « P + o(xᵏ) » : des monômes de degrés différents et un seul reste
 * (ou le reste seul).
 */
export function isReducedDL(t: Term, x: string): boolean {
  if (!hasO(t)) return false;
  const parts = t.k === 'add' ? t.c : [t];
  let os = 0;
  const degs = new Set<number>();
  for (const p of parts) {
    if (p.k === 'sym' && oOrder(p.n) !== null) {
      os++;
      continue;
    }
    if (hasO(p)) return false;
    const m = monomialOf(p, x);
    if (!m || degs.has(m.d)) return false;
    degs.add(m.d);
  }
  return os === 1;
}

/** dans une écriture réduite : un monôme de degré plus grand que l'ordre du reste */
export function beyondOrder(t: Term, x: string): { k: number; d: number } | null {
  if (!isReducedDL(t, x)) return null;
  const parts = t.k === 'add' ? t.c : [t];
  const k = maxOOrder(t);
  for (const p of parts) {
    if (p.k === 'sym') continue;
    const m = monomialOf(p, x);
    if (m && m.d > k) return { k, d: m.d };
  }
  return null;
}

/* ------------------------------ d'où vient un DL ------------------------------ */

export interface DLDerived {
  ser: Ser;
  steps: DLStep[];
  /** un développement (pas seulement un polynôme) */
  dl: boolean;
}

export interface DeriveCtx {
  x: string;
  N: number;
  /** le DL établi d'une expression exacte (null : aucun) */
  lookup: (t: Term) => Ser | null;
  /** ignorer les DL établis (on refait tout depuis les DL de référence) */
  fresh: boolean;
}

/**
 * Le développement de t et les gestes pour l'obtenir : les DL déjà établis et les
 * polynômes sont gratuits ; un DL de référence, une composée, une somme, un produit, un
 * quotient… comptent chacun. null : l'écriture ne se développe pas ainsi.
 */
export function dlDerive(t: Term, c: DeriveCtx): DLDerived | null {
  try {
    return derive(t, c);
  } catch {
    return null;
  }
}

function derive(t: Term, c: DeriveCtx): DLDerived | null {
  const { x, N } = c;
  if (t.k === 'sym') {
    const k = oOrder(t.n);
    if (k !== null) return { ser: { co: new Map(), e: k }, steps: [], dl: true };
  }
  const o = hasO(t);
  if (!hasX(t, x) && !o) {
    const s = seriesOf(t, x, N);
    return s ? { ser: s, steps: [], dl: false } : null;
  }
  if (o && isReducedDL(t, x)) {
    const s = seriesOf(t, x, N);
    return s ? { ser: s, steps: [], dl: true } : null;
  }
  if (!o && !c.fresh) {
    const known = c.lookup(t);
    if (known) return { ser: known, steps: [], dl: true };
  }
  if (!o && isPolyLike(t, x)) {
    const s = seriesOf(t, x, N);
    return s ? { ser: s, steps: [], dl: false } : null;
  }
  if (!o && isReference(t, x)) {
    const s = seriesOf(t, x, N);
    return s ? { ser: s, steps: ['dlReference'], dl: true } : null;
  }
  switch (t.k) {
    case 'add': {
      const ds = t.c.map((u) => derive(u, c));
      if (ds.some((d) => !d)) return null;
      const D = ds as DLDerived[];
      let ser: Ser = { co: new Map(), e: Infinity };
      D.forEach((d) => (ser = sAdd(ser, d.ser)));
      const steps = D.flatMap((d) => d.steps);
      const dl = D.some((d) => d.dl);
      if (dl && D.length >= 2) steps.push('dlSum');
      return { ser, steps, dl };
    }
    case 'mul': {
      const ds = t.c.map((u) => derive(u, c));
      if (ds.some((d) => !d)) return null;
      const D = ds as DLDerived[];
      let ser: Ser = { co: new Map([[0, Q.ONE]]), e: Infinity };
      D.forEach((d) => (ser = sMul(ser, d.ser)));
      const steps = D.flatMap((d) => d.steps);
      const dls = t.c.filter((_u, i) => D[i]!.dl).length;
      const polys = t.c.filter((u, i) => !D[i]!.dl && hasX(u, x)).length;
      if (dls >= 2) steps.push('dlProduct');
      else if (dls === 1 && polys >= 1) steps.push('dlScale');
      return { ser, steps, dl: dls > 0 };
    }
    case 'div': {
      const da = derive(t.a, c);
      if (!da) return null;
      const r = !hasX(t.b, x) && !hasO(t.b) ? termRat(t.b) : null;
      if (r) {
        const inv = Q.div(Q.ONE, r);
        if (!inv) return null;
        const ser = sMul(da.ser, { co: new Map([[0, inv]]), e: Infinity });
        return { ser, steps: da.steps, dl: da.dl };
      }
      const db = derive(t.b, c);
      if (!db) return null;
      const inv = sInv(db.ser, N + Math.max(0, -(lowest(da.ser) ?? 0)));
      if (!inv) return null;
      const ser = sMul(da.ser, inv);
      const steps = [...da.steps];
      if (!da.dl && !db.dl) return { ser, steps, dl: false };
      if (!db.dl && monomialOf(t.b, x)) steps.push('dlDivide');
      else steps.push(...db.steps, 'dlQuotient');
      return { ser, steps, dl: true };
    }
    case 'pow': {
      const r = termRat(t.b);
      if (!r) return null;
      const da = derive(t.a, c);
      if (!da) return null;
      const ser = serPow(da.ser, r, N);
      if (!ser) return null;
      const steps = [...da.steps];
      if (Q.isInteger(r)) {
        const n = Number(r.n);
        if (n >= 2 && da.dl) steps.push('dlProduct');
        if (n < 0 && da.dl) steps.push('dlQuotient');
        return { ser, steps, dl: da.dl };
      }
      steps.push('dlCompose');
      return { ser, steps, dl: true };
    }
    case 'fn': {
      const da = derive(t.a, c);
      if (!da) return null;
      const ser = serFn(t.f, da.ser, N);
      if (!ser) return null;
      return { ser, steps: [...da.steps, 'dlCompose'], dl: true };
    }
    default:
      return null;
  }
}

function lowest(s: Ser): number | null {
  let v: number | null = null;
  for (const [d, c] of s.co) if (!Q.isZero(c) && (v === null || d < v)) v = d;
  return v;
}

/** les gestes de a qui ne sont pas dans b (multiensembles) */
export function stepsDiff(a: DLStep[], b: DLStep[]): DLStep[] {
  const rest = [...b];
  const out: DLStep[] = [];
  for (const s of a) {
    const i = rest.indexOf(s);
    if (i >= 0) rest.splice(i, 1);
    else out.push(s);
  }
  return out;
}

/** « le terme en x² », « le terme constant », « le terme en 1 ÷ x » */
export function degreeName(d: number, x = 'x'): string {
  if (d === 0) return 'le terme constant';
  const sup = (n: number) => String(n).replace(/\d/g, (c) => SUP[Number(c)]!);
  if (d === 1) return `le terme en ${x}`;
  if (d > 1) return `le terme en ${x}${sup(d)}`;
  if (d === -1) return `le terme en 1 ÷ ${x}`;
  return `le terme en 1 ÷ ${x}${sup(-d)}`;
}
